import { HttpErrorResponse, HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { of, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { getAzureAdConfig, isAzureAdEnabled } from './azure-ad-env';
import { AuthService } from './auth.service';
import {
  MSAL_ID_TOKEN_RETRY_HEADER,
  MSAL_REDIRECT_HTTP_BODY,
  MsalTokenService,
  isMsalRedirectError
} from './msal-token.service';

function isSpiceLoginOrRefresh(url: string, auth: AuthService): boolean {
  return url.includes('/api/v1/login') || url.includes(auth.getRefreshPath());
}

function isBackendApiRequest(url: string): boolean {
  const base = environment.apiBaseUrl.replace(/\/$/, '');
  return url.startsWith(base);
}

/**
 * For Microsoft sign-in (MSAL), attaches a fresh **`id_token`** as `Authorization: Bearer …`
 * on requests to `environment.apiBaseUrl` (backend validates SSO on id_token).
 * Requests whose URL does not start with `apiBaseUrl` skip the header (static assets, other hosts).
 *
 * {@link MsalTokenService#getAccessToken} uses **login scopes** and the cache when the `id_token`
 * is still valid; on **401** or **403**, calls {@link MsalTokenService#getAccessToken} with
 * **`forceRefresh: true`** once and retries with header {@link MSAL_ID_TOKEN_RETRY_HEADER} so the
 * retry cannot loop. If the initial token acquisition throws (non-HTTP), one **`forceRefresh`**
 * attempt is made; the API is never called without a bearer.
 *
 * When MSAL requires redirect, {@link MsalTokenService} throws a sentinel error (`MSAL_REDIRECT`);
 * this interceptor emits a single **204** {@link HttpResponse} with body {@link MSAL_REDIRECT_HTTP_BODY}
 * so `firstValueFrom` / `lastValueFrom` do not throw `EmptyError`. Callers should use
 * **`isMsalRedirectResponse`** from `msal-token.service` before treating the body as API data.
 */
export const azureAdApiInterceptor: HttpInterceptorFn = (req, next) => {
  if (!isAzureAdEnabled()) {
    return next(req);
  }
  if (!isBackendApiRequest(req.url)) {
    return next(req);
  }

  const auth = inject(AuthService);
  if (isSpiceLoginOrRefresh(req.url, auth)) {
    return next(req);
  }

  if (getAzureAdConfig() == null) {
    return next(req);
  }

  const msalToken = inject(MsalTokenService);

  /** Ensures at most one 401/403 → `forceRefresh` + second HTTP attempt per API call. */
  let didHttpAuthRetry = false;

  const send = (bearer: string, isSecondHttpRequest: boolean) => {
    const headers: Record<string, string> = { Authorization: `Bearer ${bearer}` };
    if (isSecondHttpRequest) {
      headers[MSAL_ID_TOKEN_RETRY_HEADER] = 'true';
    }
    return next(req.clone({ setHeaders: headers }));
  };

  return msalToken.getAccessToken().pipe(
    switchMap((bearer) =>
      send(bearer, false).pipe(
        catchError((err: unknown) => {
          /**
           * One `forceRefresh` + retry: Entra / APIM may return 403 (not 401) when the id_token is
           * wrong or not yet valid — same params sometimes succeed (cached token vs. fresh call).
           */
          if (
            err instanceof HttpErrorResponse &&
            (err.status === 401 || err.status === 403) &&
            !didHttpAuthRetry
          ) {
            didHttpAuthRetry = true;
            return msalToken
              .getAccessToken({ forceRefresh: true })
              .pipe(switchMap((bearer2) => send(bearer2, true)));
          }
          return throwError(() => err);
        })
      )
    ),
    catchError((err: unknown) => {
      if (isMsalRedirectError(err)) {
        return of(
          new HttpResponse({
            status: 204,
            url: req.url,
            body: MSAL_REDIRECT_HTTP_BODY
          })
        );
      }
      if (err instanceof HttpErrorResponse) {
        return throwError(() => err);
      }
      /**
       * Default `getAccessToken()` failed (transient MSAL / network / cache). Do **not** call the
       * API without a bearer — that produced random **403** on endpoints like `get_optionset` while
       * other calls succeeded. Retry once with `forceRefresh`, then fail the request.
       */
      return msalToken.getAccessToken({ forceRefresh: true }).pipe(
        switchMap((bearer) => send(bearer, false)),
        catchError((err2: unknown) => {
          if (isMsalRedirectError(err2)) {
            return of(
              new HttpResponse({
                status: 204,
                url: req.url,
                body: MSAL_REDIRECT_HTTP_BODY
              })
            );
          }
          return throwError(() => err2);
        })
      );
    })
  );
};
